import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { CONFIG, WEATHER_CONFIG } from '../config';

// Déduplication pour les appels météo (absorbe StrictMode et re-rendus rapides)
const WEATHER_INFLIGHT = new Map(); // key -> Promise
const WEATHER_CACHE = new Map(); // key -> { data, expiry }
const WEATHER_TTL = 10000; // 10s - Plus long pour absorber React Strict Mode

const WeatherContext = createContext();

/**
 * Ajout : structure pour stocker la météo horaire (Open-Meteo)
 * hourlyWeather : {
 *   city: {...},
 *   hourly: { time: [...], temperature_2m: [...] },
 *   units: {...}
 * }
 */

export const useWeather = () => {
  const context = useContext(WeatherContext);
  if (!context) {
    throw new Error('useWeather must be used within a WeatherProvider');
  }
  return context;
};

export const WeatherProvider = ({ children, parkCoordinates, parkId }) => {
  // État pour la météo quotidienne (déjà existant)
  const [dailyForecast, setDailyForecast] = useState(null);
  // Nouvel état pour la météo horaire
  const [hourlyWeather, setHourlyWeather] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [errorDetails, setErrorDetails] = useState(null);
  // Ajout : état d'erreur pour la météo horaire
  const [hourlyError, setHourlyError] = useState(null);

  const fetchWeatherData = useCallback(async () => {
    if (!parkCoordinates?.lat || !parkCoordinates?.lng) {
      setLoading(false);
      return;
    }
    const key = `daily::${parkCoordinates.lat},${parkCoordinates.lng}::${parkId || 'unknown'}`;
    try {
      setLoading(true);
      const now = Date.now();
      const cached = WEATHER_CACHE.get(key);
      if (cached && now < cached.expiry) {
        setDailyForecast(cached.data);
        setError(null);
        return;
      }

      if (!WEATHER_INFLIGHT.has(key)) {
        const fetchPromise = (async () => {
          const response = await fetch(
            `${CONFIG.API_URL}/api/weather/daily?lat=${parkCoordinates.lat}&lng=${parkCoordinates.lng}&parkId=${parkId}`
          );
          if (!response.ok) {
            const errorData = await response.json().catch(() => null);
            if (response.status === 503) {
              throw new Error(errorData?.message || 'Le service météo est temporairement indisponible. Veuillez réessayer plus tard.');
            } else if (response.status === 429) {
              throw new Error(errorData?.message || 'Le nombre maximum d\'appels à l\'API météo a été atteint. Veuillez réessayer plus tard.');
            } else {
              throw new Error(errorData?.message || 'Erreur lors de la récupération des données météo');
            }
          }
          const data = await response.json();
          WEATHER_CACHE.set(key, { data, expiry: Date.now() + WEATHER_TTL });
          return data;
        })().finally(() => {
          WEATHER_INFLIGHT.delete(key);
        });
        WEATHER_INFLIGHT.set(key, fetchPromise);
      }

      const data = await WEATHER_INFLIGHT.get(key);
      if (data?.fromCache) {
        setErrorDetails({ type: 'warning', message: data.cacheNotice || 'Données météo en cache utilisées' });
      } else {
        setErrorDetails(null);
      }
      setDailyForecast(data);
      setError(null);
    } catch (err) {
      setError(err.message);
      setDailyForecast(null);
    } finally {
      setLoading(false);
    }
  }, [parkCoordinates?.lat, parkCoordinates?.lng, parkId]);

  /**
   * Ajout : récupération des prévisions horaires Open-Meteo (temperature_2m)
   * Stocke le résultat dans hourlyWeather
   */
  const fetchHourlyWeather = useCallback(async () => {
    if (!parkCoordinates?.lat || !parkCoordinates?.lng) {
      setHourlyWeather(null);
      setHourlyError(null);
      return;
    }
    const variables = 'temperature_2m,relative_humidity_2m,precipitation,snowfall,weather_code,wind_speed_10m,wind_gusts_10m';
    const key = `hourly::${parkCoordinates.lat},${parkCoordinates.lng}::${variables}::${parkId || 'unknown'}`;
    try {
      const now = Date.now();
      const cached = WEATHER_CACHE.get(key);
      if (cached && now < cached.expiry) {
        setHourlyWeather(cached.data);
        setHourlyError(null);
        return;
      }
      if (!WEATHER_INFLIGHT.has(key)) {
        const fetchPromise = (async () => {
          const response = await fetch(
            `${CONFIG.API_URL}/api/weather/hourly?lat=${parkCoordinates.lat}&lng=${parkCoordinates.lng}&variables=${encodeURIComponent(variables)}&parkId=${parkId}`
          );
          if (!response.ok) {
            const errorData = await response.json().catch(() => null);
            throw new Error(errorData?.message || 'Erreur lors de la récupération des données météo horaires');
          }
          const data = await response.json();
          WEATHER_CACHE.set(key, { data, expiry: Date.now() + WEATHER_TTL });
          return data;
        })().finally(() => {
          WEATHER_INFLIGHT.delete(key);
        });
        WEATHER_INFLIGHT.set(key, fetchPromise);
      }
      const data = await WEATHER_INFLIGHT.get(key);
      setHourlyWeather(data);
      setHourlyError(null);
    } catch (err) {
      setHourlyError(err.message);
      setHourlyWeather(null);
    }
  }, [parkCoordinates?.lat, parkCoordinates?.lng, parkId]);

  /**
 * Détermine si le vol de drone est autorisé selon les conditions météo.
 * Seuils ajustés le 26/11/2025 après analyse du 25/11 (spectacle annulé malgré conditions "favorables")
 * 
 * Ajout marge d'erreur le 12/02/2026 : 
 * - Différence entre station météo (Chessy) et position réelle (château Disneyland Paris)
 * - 25% de marge pour le vent moyen, 30% pour les rafales
 * 
 * @param {object} forecast Prévisions météo du jour (horaire ou quotidienne)
 * @returns {boolean} true si vol autorisé, false sinon
 */
const isDroneFlyable = (forecast) => {
  if (!forecast) {
    return false;
  }

  // Pour les prévisions quotidiennes, les noms de champs sont différents
  // On normalise les valeurs pour supporter les deux formats
  const windValue = forecast.wind10m || forecast.wind_speed_10m_max || 0;
  const gustValue = forecast.gust10m || forecast.windgusts_10m_max || (windValue * 1.5); // Estimation si pas de rafales
  const precipValue = forecast.rr1 || forecast.precipitation_sum || 0;
  const humidityValue = forecast.humidity;
  const tempValue = forecast.tmin !== undefined ? forecast.tmin : forecast.temperature_2m_min;
  const weatherValue = forecast.weather;

  // Application de la marge d'erreur pour le vent et les rafales
  const windMargin = WEATHER_CONFIG.THRESHOLDS.DRONE * (WEATHER_CONFIG.DRONE_MARGIN?.WIND_PERCENT || 0) / 100;
  const gustMargin = WEATHER_CONFIG.THRESHOLDS.DRONE_GUST * (WEATHER_CONFIG.DRONE_MARGIN?.GUST_PERCENT || 0) / 100;
  
  const adjustedWindThreshold = WEATHER_CONFIG.THRESHOLDS.DRONE - windMargin;
  const adjustedGustThreshold = WEATHER_CONFIG.THRESHOLDS.DRONE_GUST - gustMargin;

  const isTooWindy = windValue > adjustedWindThreshold;
  const isTooGusty = gustValue > adjustedGustThreshold;
  const isRaining = precipValue > WEATHER_CONFIG.THRESHOLDS.RAIN;
  
  // Nouveaux seuils ajoutés le 26/11/2025
  const isTooHumid = humidityValue !== undefined && humidityValue > WEATHER_CONFIG.THRESHOLDS.DRONE_HUMIDITY;
  const isTooCold = tempValue !== undefined && tempValue < WEATHER_CONFIG.THRESHOLDS.DRONE_TEMP_MIN;
  
  // Orage : code 15 (Open-Meteo) ou codes étendus dans WEATHER_CONFIG.WEATHER_CODES.THUNDERSTORM
  const hasThunderstorm = [15, 95, 96, 99].includes(weatherValue) || (WEATHER_CONFIG.WEATHER_CODES && WEATHER_CONFIG.WEATHER_CODES.THUNDERSTORM?.includes(weatherValue));
  
  // Neige et verglas : codes 71, 73, 75 (neige), 77 (grains), 85, 86 (averses neige), 56, 57 (bruine verglaçante), 66, 67 (pluie verglaçante)
  const hasSnowOrIce = [56, 57, 66, 67, 71, 73, 75, 77, 85, 86].includes(weatherValue);

  return !isTooWindy && !isTooGusty && !isRaining && !hasThunderstorm && !hasSnowOrIce && !isTooHumid && !isTooCold;
};

/**
 * Retourne le statut détaillé du vol de drone
 * @param {object} forecast Prévisions météo du jour
 * @returns {object} { canFly: boolean, inMarginZone: boolean, reasons: string[] }
 */
const getDroneStatus = (forecast) => {
  if (!forecast) {
    return { canFly: false, inMarginZone: false, reasons: [] };
  }

  const windMargin = WEATHER_CONFIG.THRESHOLDS.DRONE * (WEATHER_CONFIG.DRONE_MARGIN?.WIND_PERCENT || 0) / 100;
  const gustMargin = WEATHER_CONFIG.THRESHOLDS.DRONE_GUST * (WEATHER_CONFIG.DRONE_MARGIN?.GUST_PERCENT || 0) / 100;
  
  const adjustedWindThreshold = WEATHER_CONFIG.THRESHOLDS.DRONE - windMargin;
  const adjustedGustThreshold = WEATHER_CONFIG.THRESHOLDS.DRONE_GUST - gustMargin;
  
  const originalWindThreshold = WEATHER_CONFIG.THRESHOLDS.DRONE;
  const originalGustThreshold = WEATHER_CONFIG.THRESHOLDS.DRONE_GUST;

  const reasons = [];
  let inMarginZone = false;
  
  // Pour les prévisions quotidiennes, gust10m peut ne pas exister
  // On utilise alors une estimation basée sur le vent moyen
  const windValue = forecast.wind10m || forecast.wind_speed_10m_max || 0;
  const gustValue = forecast.gust10m || forecast.windgusts_10m_max || (windValue * 1.5); // Estimation si pas de rafales
  const precipValue = forecast.rr1 || forecast.precipitation_sum || 0;
  const humidityValue = forecast.humidity;
  const tempValue = forecast.tmin !== undefined ? forecast.tmin : forecast.temperature_2m_min;
  const weatherValue = forecast.weather;
  
  // Vérification du vent
  if (windValue > adjustedWindThreshold) {
    if (windValue <= originalWindThreshold) {
      inMarginZone = true;
      reasons.push('vent_marge');
    } else {
      reasons.push('vent_trop_fort');
    }
  }
  
  // Vérification des rafales
  if (gustValue > adjustedGustThreshold) {
    if (gustValue <= originalGustThreshold) {
      inMarginZone = true;
      reasons.push('rafales_marge');
    } else {
      reasons.push('rafales_trop_fortes');
    }
  }
  
  // Vérification pluie
  if (precipValue > WEATHER_CONFIG.THRESHOLDS.RAIN) {
    reasons.push('pluie');
  }
  
  // Vérification humidité
  if (humidityValue !== undefined && humidityValue > WEATHER_CONFIG.THRESHOLDS.DRONE_HUMIDITY) {
    reasons.push('humidite');
  }
  
  // Vérification température
  if (tempValue !== undefined && tempValue < WEATHER_CONFIG.THRESHOLDS.DRONE_TEMP_MIN) {
    reasons.push('temperature');
  }
  
  // Vérification orage
  const hasThunderstorm = [15, 95, 96, 99].includes(weatherValue) || (WEATHER_CONFIG.WEATHER_CODES && WEATHER_CONFIG.WEATHER_CODES.THUNDERSTORM?.includes(weatherValue));
  if (hasThunderstorm) {
    reasons.push('orage');
  }
  
  // Vérification neige/verglas
  const hasSnowOrIce = [56, 57, 66, 67, 71, 73, 75, 77, 85, 86].includes(weatherValue);
  if (hasSnowOrIce) {
    reasons.push('neige_verglas');
  }

  const canFly = reasons.length === 0;
  
  return { canFly, inMarginZone, reasons };
};

  // Effet pour récupérer la météo quotidienne ET horaire au montage et lors des changements de coordonnées
  // Protection renforcée contre React Strict Mode avec un seul appel initial
  const initialFetchDoneRef = React.useRef(false);
  const currentCoordsRef = React.useRef(null);

  useEffect(() => {
    // Ne rien faire si pas de coordonnées (évite les appels inutiles au montage)
    if (!parkCoordinates?.lat || !parkCoordinates?.lng) {
      initialFetchDoneRef.current = false;
      currentCoordsRef.current = null;
      setLoading(false);
      setDailyForecast(null);
      setHourlyWeather(null);
      setError(null);
      setHourlyError(null);
      return;
    }

    const coordsKey = `${parkCoordinates.lat},${parkCoordinates.lng},${parkId}`;
    const coordsChanged = currentCoordsRef.current !== coordsKey;

    // Appeler seulement si les coordonnées ont changé ou si c'est le tout premier appel
    if (coordsChanged || !initialFetchDoneRef.current) {
      currentCoordsRef.current = coordsKey;
      initialFetchDoneRef.current = true;
      
      fetchWeatherData();
      fetchHourlyWeather();
    }
    
    // Rafraîchir toutes les 15 minutes seulement si on a des coordonnées valides
    const interval1 = setInterval(fetchWeatherData, WEATHER_CONFIG.REFRESH_INTERVAL);
    const interval2 = setInterval(fetchHourlyWeather, WEATHER_CONFIG.REFRESH_INTERVAL);
    
    return () => {
      clearInterval(interval1);
      clearInterval(interval2);
    };
  }, [parkCoordinates?.lat, parkCoordinates?.lng, parkId, fetchWeatherData, fetchHourlyWeather]);

  // Expose les données horaires dans le contexte
  const value = {
    dailyForecast,
    hourlyWeather,
    loading,
    error,
    errorDetails,
    hourlyError,
    isDroneFlyable,
    getDroneStatus,
    refreshWeather: fetchWeatherData,
    refreshHourlyWeather: fetchHourlyWeather,
    parkCoordinates,
    parkId
  };

  return (
    <WeatherContext.Provider value={value}>
      {children}
    </WeatherContext.Provider>
  );
}; 